import {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from "react";
import { motion } from "motion/react";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import { useMediaQuery } from "react-responsive";
import useWindowSize from "../../hooks/useWindowSize";
import { useOverlayContext } from "../../functional-components/OverlayContext";
import ErrorBoundary from "../../functional-components/ErrorBoundary";
import { ErrorDisplaySmall } from "../ErrorDisplay";
import Icon from "../Icon";
import SimpleLoadingSpinner from "../SimpleLoadingSpinner";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import { addNoOverlayState } from "../../utils/UrlUtils";
import { RemoveScroll } from "react-remove-scroll";
import "./style.scss";

export const OverlayCover = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  return (
    <>
      <motion.div
        id="overlay-cover"
        aria-hidden
        style={{
          top: "var(--padding-top)"
        }}
        initial={{ opacity: 0, pointerEvents: "none" }}
        animate={{ opacity: 1, pointerEvents: "all" }}
        exit={{ opacity: 0, pointerEvents: "none" }}
        onClick={e => {
          if ((e.target as HTMLDivElement).id === "overlay-cover") {
            console.log("close overlay - click overlay-cover");
            navigate(-1);
          }
          e.stopPropagation();
        }}
      ></motion.div>
      {children}
    </>
  );
};

const SuspenseFallback = ({ onSuspeseEnd }: { onSuspeseEnd: () => void }) => {
  useLayoutEffect(() => {
    return () => {
      onSuspeseEnd();
    };
  });

  return <SimpleLoadingSpinner />;
};

const GenericOverlay = ({
  id,
  noPadding = false,
  hasSectionNav = false,
  children
}: {
  id: string;
  noPadding?: boolean;
  hasSectionNav?: boolean;
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSmall = useMediaQuery({ maxWidth: 767.98 });
  const isTouchDevice = useMediaQuery({ query: "(pointer: coarse)" });
  const winDim = useWindowSize();
  const { setOverlayOpened } = useOverlayContext();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOverlayOpened(true);
    return () => {
      setOverlayOpened(false);
    };
  }, [setOverlayOpened]);

  const onContentReady = useCallback(() => {
    setTimeout(() => {
      if (
        !isTouchDevice &&
        contentRef.current?.getElementsByTagName("input").item(0)
      )
        contentRef.current?.getElementsByTagName("input").item(0)?.select();
      else if (contentRef.current?.getElementsByTagName("button").item(0))
        contentRef.current?.getElementsByTagName("button").item(0)?.focus();
      else if (contentRef.current?.getElementsByTagName("a").item(0))
        contentRef.current?.getElementsByTagName("a").item(0)?.focus();
    }, 1);
  }, [isTouchDevice]);

  useEffect(() => {
    onContentReady();
  }, [onContentReady]);

  return (
    <>
      <motion.div
        id={id ? id : "overlay-window"}
        role="dialog"
        aria-modal
        className={classNames("overlay-window", { "no-padding": noPadding })}
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        exit={{ opacity: 0, scale: 0.7 }}
        transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
        style={{
          ...(isSmall
            ? {}
            : {
                top: "calc(1rem + var(--padding-top))",
                bottom: 16,
                height: "auto",
                left:
                  winDim.innerWidth > 1400 ? winDim.innerWidth / 2 - 650 : 16,
                right:
                  winDim.innerWidth > 1400 ? winDim.innerWidth / 2 - 650 : 16
              }),
          borderRadius: isSmall ? 0 : "1rem"
        }}
      >
        <RemoveScroll removeScrollBar={false} enabled forwardProps>
          <div className="overlay-content" ref={contentRef}>
            <ErrorBoundary
              boundaryId={"overlay-" + (id ? id : "unspecified")}
              fallback={<ErrorDisplaySmall />}
            >
              <Suspense
                fallback={<SuspenseFallback onSuspeseEnd={onContentReady} />}
              >
                {children}
              </Suspense>
            </ErrorBoundary>
            <KeyboardEventHandler
              handleKeys={["esc"]}
              handleFocusableElements
              onKeyEvent={(key, e) => {
                switch (key) {
                  case "esc":
                    navigate(-1);
                    e.preventDefault();
                    break;
                  default:
                }
              }}
            />
          </div>
        </RemoveScroll>
        <div
          className="overlay-button-bar"
          style={{
            ...(isSmall
              ? {
                  top: "1.25rem",
                  right: "1.5rem"
                }
              : {
                  top: hasSectionNav ? "1rem" : "1.75rem",
                  right: "1.5rem"
                })
          }}
        >
          <button
            className={classNames("overlay-button", {
              "no-padding": noPadding
            })}
            id={"overlay-close"}
            onClick={() => {
              navigate(-1);
            }}
          >
            <Icon name="xmark" />
          </button>
          {!isSmall && (
            <button
              className={classNames("overlay-button", {
                "no-padding": noPadding
              })}
              id={"overlay-maximize"}
              onClick={() => {
                navigate(
                  { pathname: location.pathname },
                  {
                    replace: true,
                    state: addNoOverlayState(location.state)
                  }
                );
              }}
            >
              <Icon name="expand" />
            </button>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default GenericOverlay;
